import React, { useState, useEffect, useRef, useCallback } from "react";
import ActiveFolder from "./pages/ActiveFolder";
import { DetailBreadcrumb } from "./components/Breadcrumb";
import { DetailPageMainWrapper, DetailPageContainer, BackToTopButton } from "./DetailPage.styled";
import { useDrivesStores } from "./hook/use-drives.hook";
import { Button } from '@ann/web-components';

const DetailPageContent = () => {
    const { drivesViewStore } = useDrivesStores();

    // Local scroll management for BackToTop button
    const [showBackToTop, setShowBackToTop] = useState(false);
    const scrollContainerRef = useRef(null);

    // useRef doesn't cause re-renders and maintains value between renders
    const drivesInitialized = useRef(false);

    // ✅ Load drives once when parent component mounts
    // Drives are already loaded by DrivesPage parent, but we check just in case
    useEffect(() => {
        const loadDrives = async () => {
            if (!drivesInitialized.current && drivesViewStore?.loadDrivesData) {
                const hasDrives = drivesViewStore?.drivesStore?.drives?.length > 0;
                if (!hasDrives) {
                    await drivesViewStore.loadDrivesData();
                }
                drivesInitialized.current = true;
            }
        };
        loadDrives();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // ✅ Executes only once on mount

    // Setup scroll listener for BackToTop button
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const SCROLL_THRESHOLD = 300; // Show button after scrolling 300px

        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            setShowBackToTop(scrollTop > SCROLL_THRESHOLD);
        };

        // Add scroll listener
        container.addEventListener('scroll', handleScroll, { passive: true });

        // Initial check
        handleScroll();

        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll to top function
    const scrollToTop = useCallback(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, []);

    return (
        <>
            <DetailPageMainWrapper ref={scrollContainerRef}>
                <DetailPageContainer>
                    <DetailBreadcrumb />
                    <ActiveFolder />
                </DetailPageContainer>
            </DetailPageMainWrapper>

            <BackToTopButton $visible={showBackToTop}>
                <Button
                    type="primary"
                    size="medium"
                    label="Back to top"
                    icon="chevronUp"
                    iconPosition="left"
                    onClick={scrollToTop}
                    pill={true}
                />
            </BackToTopButton>
        </>
    );
};

DetailPageContent.displayName = 'DetailPageContent';

// ✅ Provider moved to index.jsx to share store across all routes
export default DetailPageContent;